import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { NEUTRAL, RADIUS, SELECTED_EDGE, VIOLET } from "../../styles/brandTokens";
import { cardSx, text } from "../monitor/SummaryPanel";
import type { EmptyStateKind } from "./theoOverviewModel";

const COPY: Record<EmptyStateKind, { title: string; body: string }> = {
  new: {
    title: "Nothing to show yet",
    body:
      "When Theo starts working tickets, on its own through an Agent or with your technicians in ticket chat and Chat Assistant, you'll see what it handled and the time it saved here.",
  },
  filtered: {
    title: "Nothing in this view",
    body: "Theo has no work that matches these filters. Try a longer time range or clear the filters.",
  },
};

const quietLinkSx = {
  ...text(14, VIOLET.main, 600),
  textTransform: "none" as const,
  borderRadius: `${RADIUS.control}px`,
  px: 1.5,
  "&:hover": { background: NEUTRAL.hover, color: VIOLET.dark },
  "&:focus-visible": { boxShadow: SELECTED_EDGE },
};

interface OverviewEmptyStateProps {
  kind: EmptyStateKind;
  /** Offers agent setup only to tenants with Auto Mode. */
  canSetUpAgents: boolean;
  onShowAll: () => void;
}

const OverviewEmptyState: React.FC<OverviewEmptyStateProps> = ({ kind, canSetUpAgents, onShowAll }) => (
  <Box
    component="section"
    aria-labelledby="overview-empty"
    sx={{ ...cardSx, px: { xs: 3, md: 6 }, py: { xs: 5, md: 7 }, textAlign: "center" }}
  >
    <Typography id="overview-empty" component="h2" sx={text(20, NEUTRAL.text, 700)}>
      {COPY[kind].title}
    </Typography>
    <Typography sx={{ ...text(14.5, NEUTRAL.textSecondary), maxWidth: 520, mx: "auto", mt: 1 }}>
      {COPY[kind].body}
    </Typography>
    <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1, mt: 3 }}>
      {kind === "new" ? (
        <>
          <Button
            component={RouterLink}
            to="/chat-assistant"
            variant="contained"
            disableElevation
            sx={{
              ...text(14, "#fff", 600),
              textTransform: "none",
              borderRadius: `${RADIUS.control}px`,
              background: VIOLET.main,
              "&:hover": { background: VIOLET.dark },
            }}
          >
            Open Chat Assistant
          </Button>
          {canSetUpAgents ? (
            <Button component={RouterLink} to="/theo-automations/configure" sx={quietLinkSx}>
              Set up an Agent
            </Button>
          ) : null}
        </>
      ) : (
        <Button onClick={onShowAll} sx={quietLinkSx}>
          Show all time
        </Button>
      )}
    </Box>
  </Box>
);

export default OverviewEmptyState;
